/**
 * 准备度评估（§8 / §29.1）。
 *
 * 严格对照规格伪代码，优先级为：
 *   脚部明显变差 > 脚部略差 > 多信号(≥2) > 单信号(1) > 正常。
 *
 * 输出 ReadinessResult（ReadinessAdjustmentSnapshot 的超集），字段：
 *   planType / allowProgression / volumeMultiplier / loadMultiplier /
 *   requireExerciseRefilter / allowFailure / reasonCodes
 *
 * 确定性规则：纯函数、无 AI、无网络、无系统时钟。
 */

import type { ReadinessInput, ReadinessResult } from "./interface";
import type { ProgressionReasonCode } from "@jilian/shared";

export function evaluateReadiness(input: ReadinessInput): ReadinessResult {
  if (input.footStatus === "significantly_worse") {
    // §6.5 / §8「脚部明显变差」：暂停正常生成，提供保守坐姿/卧姿模板（另需用户二次确认）
    return {
      planType: "injury_conservative",
      allowProgression: false,
      volumeMultiplier: 0.5,
      loadMultiplier: 0.9,
      requireExerciseRefilter: true,
      allowFailure: false,
      reasonCodes: ["FOOT_SIGNIFICANTLY_WORSE"],
    };
  }

  let badSignals = 0;
  if (input.sleepQuality === "poor") badSignals++;
  if (input.fatigueLevel === "high") badSignals++;
  if (input.muscleSoreness === "significant") badSignals++;
  if (input.jointPain === "present") badSignals++;

  if (input.footStatus === "slightly_worse") {
    // §8「脚部略差」：暂停加重、只保留低足部参与动作、默认删除站姿稳定动作
    return {
      planType: "injury_reduced",
      allowProgression: false,
      volumeMultiplier: 0.7,
      loadMultiplier: 1,
      requireExerciseRefilter: true,
      allowFailure: false,
      reasonCodes: ["FOOT_SLIGHTLY_WORSE"],
    };
  }

  if (badSignals >= 2) {
    // §8「明显降量」：总组数 -30%～-40%，可删一个次要动作，重量 -5%～-10%，RIR 2–3，不自动加重
    return {
      planType: "reduced",
      allowProgression: false,
      volumeMultiplier: 0.65,
      loadMultiplier: 0.9,
      requireExerciseRefilter: input.jointPain === "present",
      allowFailure: false,
      reasonCodes: ["MULTIPLE_RECOVERY_WARNINGS"],
    };
  }

  if (badSignals === 1) {
    // §8「轻微降量」：总组数 -20%～-30%，不额外加组
    return {
      planType: "lightly_reduced",
      allowProgression: false,
      volumeMultiplier: 0.8,
      loadMultiplier: 1,
      requireExerciseRefilter: input.jointPain === "present",
      allowFailure: false,
      reasonCodes: ["ONE_RECOVERY_WARNING"],
    };
  }

  return {
    planType: "normal",
    allowProgression: true,
    volumeMultiplier: 1,
    loadMultiplier: 1,
    requireExerciseRefilter: false,
    allowFailure: true,
    reasonCodes: ["READY_NORMAL"],
  };
}

/**
 * 降量原因码 → 追加到进阶判定时使用的计划类型原因（reasons.ts 展示用）。
 * 供 decideProgression 在「REDUCED_DAY_NO_PROGRESSION」之外保留脚部/恢复信号。
 */
export function readinessReasonCodes(result: ReadinessResult): ProgressionReasonCode[] {
  return result.reasonCodes as ProgressionReasonCode[];
}